import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Info } from "lucide-react";
import { WithdrawalRulesDemo } from "./WithdrawalRulesDemo";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { panelAccentDot, panelPill } from "@/lib/panel-ui";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function Rule({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 tabular-nums">
          {n}
        </span>
        <p className="text-xs font-semibold text-foreground leading-snug">{title}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed pl-7">{children}</p>
    </div>
  );
}

export function WithdrawalRulesModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation("producerFinancial");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent mobilePresentation="fullscreen" className="max-w-5xl p-0 overflow-hidden rounded-[28px] border border-border/70 bg-white dark:bg-[#18181b] shadow-2xl">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-border/50">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight text-foreground font-display flex items-center gap-2">
            <span className={panelAccentDot} />
            {t("withdrawalRules.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("withdrawalRules.subtitle")}
          </p>
        </div>

        <div className="max-h-[75vh] sm:max-h-[72vh] overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_324px] lg:grid-rows-[minmax(0,1fr)]">
          {/* Esquerda: linha do tempo animada */}
          <div className="p-3 sm:p-4 lg:p-5 lg:flex lg:items-center lg:min-h-0">
            <WithdrawalRulesDemo />
          </div>

          {/* Direita: as regras em texto */}
          <div className="border-t lg:border-t-0 lg:border-l border-border/50 bg-secondary/15 lg:min-h-0 lg:overflow-y-auto">
            <div className="p-4 space-y-3.5">
              <Rule n={1} title={t("withdrawalRules.rule1Title")}>
                {t("withdrawalRules.rule1Desc")}
              </Rule>

              <Rule n={2} title={t("withdrawalRules.rule2Title")}>
                {t("withdrawalRules.rule2Desc")}
              </Rule>

              <Rule n={3} title={t("withdrawalRules.rule3Title")}>
                {t("withdrawalRules.rule3Desc")}
              </Rule>

              <div className="flex items-start gap-2 p-2.5 rounded-2xl border border-dashed border-border/60 bg-background/60">
                <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {t("withdrawalRules.infoText1")}
                  <span className="text-foreground font-medium">{t("withdrawalRules.infoText2")}</span>
                  {t("withdrawalRules.infoText3")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-t border-border/50 bg-secondary/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground text-center sm:text-left">{t("withdrawalRules.footerText")}</p>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={cn(
              panelPill,
              "rounded-full h-11 sm:h-10 px-6 text-sm sm:text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all shrink-0 active:scale-[0.97] w-full sm:w-auto"
            )}
          >
            {t("withdrawalRules.gotIt")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

